import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";
import {
	ANALYTICS_CONSENT_EVENT,
	analyticsConsentGranted,
	cookieConsentActive,
} from "../features/cookie-consent/consent.js";
import {
	createFunnelAttempt,
	validateFunnelEvent,
	type FunnelDimensions,
	type FunnelName,
} from "./analytics-events.js";

const GA_MEASUREMENT_ID = "G-12VTHVJK7H";
const PROD_HOSTNAME = "app.makerdoc.ai";

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

export function isAnalyticsAllowed(): boolean {
	return (
		typeof window !== "undefined" && window.location.hostname === PROD_HOSTNAME
	);
}

export function isAdminPath(pathname: string): boolean {
	return pathname === "/admin" || pathname.startsWith("/admin/");
}

export function analyticsPage(pathname: string): string | null {
	if (
		[
			"/",
			"/docs",
			"/onboarding",
			"/brand",
			"/team",
			"/billing",
			"/privacy",
			"/terms",
		].includes(pathname)
	)
		return pathname;
	if (/^\/docs\/[^/]+$/.test(pathname)) return "/docs/deck";
	return null;
}

let initialized = false;
let lastPage: string | null = null;
let consentEpoch = 0;

function permitted(): boolean {
	return (
		isAnalyticsAllowed() && analyticsPage(window.location.pathname) !== null
	);
}

function pageContext(page: string) {
	return {
		page_path: page,
		page_location: `https://${PROD_HOSTNAME}${page}`,
		page_title: "MakerDoc",
		page_referrer: "",
	};
}

function initAnalytics(): void {
	if (initialized || !permitted()) return;
	initialized = true;
	window.dataLayer = window.dataLayer || [];
	window.gtag = function gtag() {
		// biome-ignore lint/complexity/noArguments: gtag.js contract requires the arguments object
		window.dataLayer?.push(arguments);
	};
	window.gtag("js", new Date());
	const euGated = cookieConsentActive();
	window.gtag("consent", "default", {
		ad_storage: "denied",
		ad_user_data: "denied",
		ad_personalization: "denied",
		analytics_storage: euGated ? "denied" : "granted",
		...(euGated ? { wait_for_update: 500 } : {}),
	});
	window.gtag("config", GA_MEASUREMENT_ID, {
		send_page_view: false,
		allow_google_signals: false,
		allow_ad_personalization_signals: false,
		...pageContext(analyticsPage(window.location.pathname) ?? "/"),
	});
	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
	document.head.appendChild(script);
	if (euGated && analyticsConsentGranted()) updateConsent(true);
}

function updateConsent(granted: boolean): void {
	if (!initialized) return;
	window.gtag?.("consent", "update", {
		analytics_storage: granted ? "granted" : "denied",
	});
}

export function trackFunnelEvent(name: unknown, payload: unknown): boolean {
	if (!permitted() || !validateFunnelEvent(name, payload)) return false;
	initAnalytics();
	window.gtag?.("event", name, {
		...payload,
		...pageContext(analyticsPage(window.location.pathname) ?? "/"),
	});
	return true;
}

export function beginFunnel<Name extends FunnelName>(
	name: Name,
	dimensions: FunnelDimensions<Name>,
	onSettled?: () => void,
) {
	const epoch = consentEpoch;
	const path = typeof window === "undefined" ? "" : window.location.pathname;
	return createFunnelAttempt(
		name,
		dimensions,
		trackFunnelEvent,
		() =>
			permitted() &&
			epoch === consentEpoch &&
			window.location.pathname === path,
		onSettled,
	);
}

export function trackPageview(pathname: string): void {
	const page = analyticsPage(pathname);
	if (!permitted() || !page) {
		lastPage = null;
		return;
	}
	initAnalytics();
	if (lastPage === pathname) return;
	lastPage = pathname;
	window.gtag?.("set", pageContext(page));
	window.gtag?.("event", "page_view", pageContext(page));
}

export function useAnalytics(): void {
	const pathname = useLocation({ select: (location) => location.pathname });
	useEffect(() => {
		if (!isAnalyticsAllowed()) return;
		initAnalytics();
		const onChange = () => {
			consentEpoch += 1;
			const granted = analyticsConsentGranted();
			updateConsent(granted);
			if (granted) {
				lastPage = null;
				trackPageview(window.location.pathname);
			}
		};
		window.addEventListener(ANALYTICS_CONSENT_EVENT, onChange);
		return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, onChange);
	}, []);
	useEffect(() => trackPageview(pathname), [pathname]);
}
